import FW from '../fw/fw';
import Env from '../fw/env';
import Logger from '../fw/logger';


export default class FullscreenManagement {

  #rmp;
  #iosWebkitbeginfullscreenFn = null;
  #iosWebkitendfullscreenFn = null;
  #fullscreenchangeFn = null;
  #manageOrientationChangeFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #manageOrientationChange() {
    if (window.screen.orientation.type.startsWith('landscape')) {
      Logger.print('info', 'orientation changes to landscape > enter fullscreen');
      this.#rmp.setFullscreen(true).catch(error => {
        this.#rmp.rmpUtils.warning(1017, error);
      });
      // Let's force landscape mode only if device is in portrait mode and can be held in one hand (e.g. not tablet)
      window.screen.orientation.lock('landscape').then(() => {
        Logger.print('info', 'fullscreen requested > lock landscape');
      }).catch(error => {
        this.#rmp.rmpUtils.warning(1028, error);
      });
    } else {
      window.screen.orientation.unlock();
    }
  }

  #fullscreenButtonInteraction(event) {
    FW.eventStopPrevent(event);
    this.#rmp.rmpUIManagement.showChrome();
    this.executeFullscreenRequest();
    if (this.#rmp.automaticLandscapeOnFullScreen && Env.hasScreenOrientationLockAPI) {
      if (!this.#rmp.isInFullscreen) {
        // Let's force landscape mode only if device is in portrait mode and can be held in one hand (e.g. not tablet)
        window.screen.orientation.lock('landscape').then(() => {
          Logger.print('info', 'fullscreen requested > lock landscape');
        }).catch(error => {
          this.#rmp.rmpUtils.warning(1028, error);
        });
      } else {
        window.screen.orientation.unlock();
      }
    }
  }

  #fullscreenchange() {
    // multiple instance = only one in FS
    const exitFullscreenLabel = this.#rmp.labels.hint.exitFullscreen;
    const enterFullscreenLabel = this.#rmp.labels.hint.enterFullscreen;
    const keyboardShortcutLabel = this.#rmp.labels.hint.keyboardShortcut;
    const container = this.#rmp.dom.container;
    if (!this.#rmp.isInFullscreen) {
      this.#rmp.isInFullscreen = true;
      this.#rmp.dom.fullscreen.classList.replace('rmp-i-resize-full', 'rmp-i-resize-small');
      container.classList.add('rmp-fullscreen-on');
      this.#rmp.dom.fullscreen.setAttribute(
        'aria-label',
        exitFullscreenLabel + ' ' + keyboardShortcutLabel + ' f'
      );
      FW.setText(this.#rmp.dom.fullscreenHint, exitFullscreenLabel + ' (f)');
      this.#rmp.resize();
      if (Env.isIpadOS) {
        container.classList.add('rmp-fullscreen-ipad-os');
      }
      this.#rmp.dispatch('enterfullscreen');
    } else {
      this.#rmp.isInFullscreen = false;
      this.#rmp.dom.fullscreen.classList.replace('rmp-i-resize-small', 'rmp-i-resize-full');
      container.classList.remove('rmp-fullscreen-on');
      this.#rmp.dom.fullscreen.setAttribute(
        'aria-label',
        enterFullscreenLabel + ' ' + keyboardShortcutLabel + ' f'
      );
      FW.setText(this.#rmp.dom.fullscreenHint, enterFullscreenLabel + ' (f)');
      this.#rmp.resize();
      if (Env.isIpadOS) {
        container.classList.remove('rmp-fullscreen-ipad-os');
      }
      this.#rmp.dispatch('exitfullscreen');
    }
  }

  #webkitbeginfullscreen() {
    this.#rmp.isInFullscreen = true;
    Logger.print('size', 'webkitbeginfullscreen');
    if (Env.isIos[0]) {
      this.#rmp.dom.container.classList.add('rmp-fullscreen-ios');
    }
    this.#rmp.dispatch('enterfullscreen');
  }

  #webkitendfullscreen() {
    this.#rmp.isInFullscreen = false;
    // we need to resize in case an orientation change has 
    // happened while in FS
    this.#rmp.resize();
    Logger.print('size', 'webkitendfullscreen');
    if (Env.isIos[0]) {
      this.#rmp.dom.container.classList.remove('rmp-fullscreen-ios');
    }
    this.#rmp.dispatch('exitfullscreen');
  }

  #requestFullscreen(container, video) {
    if (container && video) {
      if (typeof container.requestFullscreen !== 'undefined') {
        container.requestFullscreen({ navigationUI: 'hide' }).
          then(() => {
            this.#rmp.resolvePromise('fullscreen');
          }).
          catch(warning => {
            this.#rmp.rmpUtils.warning(1020, warning);
            this.#rmp.rejectPromise('fullscreen', this.#rmp.warningData);
          });
      } else if (typeof container.webkitRequestFullscreen !== 'undefined') {
        container.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
        this.#rmp.resolvePromise('fullscreen');
      } else if (typeof container.mozRequestFullScreen !== 'undefined') {
        container.mozRequestFullScreen();
        this.#rmp.resolvePromise('fullscreen');
      } else if (typeof container.msRequestFullscreen !== 'undefined') {
        container.msRequestFullscreen();
        this.#rmp.resolvePromise('fullscreen');
      } else if (video && typeof video.webkitEnterFullscreen !== 'undefined') {
        video.webkitEnterFullscreen();
        this.#rmp.resolvePromise('fullscreen');
      }
    }
  }

  #exitFullscreen(video) {
    if (typeof document.exitFullscreen !== 'undefined') {
      document.exitFullscreen().
        then(() => {
          this.#rmp.resolvePromise('fullscreen');
        }).
        catch(warning => {
          this.#rmp.rmpUtils.warning(1021, warning);
          this.#rmp.rejectPromise('fullscreen', this.#rmp.warningData);
        });
    } else if (typeof document.webkitExitFullscreen !== 'undefined') {
      document.webkitExitFullscreen();
      this.#rmp.resolvePromise('fullscreen');
    } else if (typeof document.mozCancelFullScreen !== 'undefined') {
      document.mozCancelFullScreen();
      this.#rmp.resolvePromise('fullscreen');
    } else if (typeof document.msExitFullscreen !== 'undefined') {
      document.msExitFullscreen();
      this.#rmp.resolvePromise('fullscreen');
    } else if (video && typeof video.webkitExitFullscreen !== 'undefined') {
      video.webkitExitFullscreen();
      this.#rmp.resolvePromise('fullscreen');
    }
  }

  wireIosFullscreen() {
    const video = this.#rmp.dom.video;
    this.#iosWebkitbeginfullscreenFn = this.#webkitbeginfullscreen.bind(this);
    video.addEventListener('webkitbeginfullscreen', this.#iosWebkitbeginfullscreenFn);
    this.#iosWebkitendfullscreenFn = this.#webkitendfullscreen.bind(this);
    video.addEventListener('webkitendfullscreen', this.#iosWebkitendfullscreenFn);
  }

  unwireIosFullscreen() {
    const video = this.#rmp.dom.video;
    video.removeEventListener('webkitbeginfullscreen', this.#iosWebkitbeginfullscreenFn);
    video.removeEventListener('webkitendfullscreen', this.#iosWebkitendfullscreenFn);
  }

  executeFullscreenRequest() {
    const exitFullscreenLabel = this.#rmp.labels.hint.exitFullscreen;
    const enterFullscreenLabel = this.#rmp.labels.hint.enterFullscreen;
    const video = this.#rmp.dom.video;
    const container = this.#rmp.dom.container;
    if (this.#rmp.hasNativeFullscreenSupport) {
      if (this.#rmp.isInFullscreen) {
        if (this.#rmp.ads && this.#rmp.adParser === 'rmp-vast' && this.#rmp.linearAdOnStage && this.#rmp.rmpVast) {
          this.#exitFullscreen(this.#rmp.rmpVast.adPlayer);
        } else {
          this.#exitFullscreen(video);
        }
      } else {
        if (this.#rmp.ads && this.#rmp.adParser === 'rmp-vast' && this.#rmp.linearAdOnStage && this.#rmp.rmpVast) {
          this.#requestFullscreen(container, this.#rmp.rmpVast.adPlayer);
        } else {
          this.#requestFullscreen(container, video);
        }
      }
    } else if (this.#rmp.fullscreenFullWindowMode) {
      Logger.print('size', 'fullscreenFullWindowMode handling');
      if (!this.#rmp.isInFullscreen) {
        if (Env.viewportSize.width > 0 && Env.viewportSize.height > 0) {
          this.#rmp.isInFullscreen = true;
          this.#rmp.dom.fullscreen.classList.replace('rmp-i-resize-full', 'rmp-i-resize-small');
          container.classList.add('rmp-full-window-on');
          this.#rmp.dom.fullscreenHint.setAttribute('aria-label', exitFullscreenLabel);
          FW.setText(this.#rmp.dom.fullscreenHint, exitFullscreenLabel + ' (f)');
          this.#rmp.dispatch('enterfullscreen');
          const newPlayerSize = {
            width: Env.viewportSize.width,
            height: Env.viewportSize.height,
            fullscreenFullWindowMode: true
          };
          this.#rmp.setPlayerSize(newPlayerSize);
          this.#rmp.resolvePromise('fullscreen');
        }
      } else {
        if (this.#rmp.rmpSizeManagement.initialWidth > 0 && this.#rmp.rmpSizeManagement.initialHeight > 0) {
          this.#rmp.isInFullscreen = false;
          this.#rmp.dom.fullscreen.classList.replace('rmp-i-resize-small', 'rmp-i-resize-full');
          container.classList.remove('rmp-full-window-on');
          this.#rmp.dom.fullscreenHint.setAttribute('aria-label', enterFullscreenLabel);
          FW.setText(this.#rmp.dom.fullscreenHint, enterFullscreenLabel + ' (f)');
          this.#rmp.dispatch('exitfullscreen');
          const newPlayerSize = {
            width: this.#rmp.rmpSizeManagement.initialWidth,
            height: this.#rmp.rmpSizeManagement.initialHeight
          };
          this.#rmp.setPlayerSize(newPlayerSize);
          // needed in case viewport is now smaller than original player width
          window.setTimeout(() => {
            this.#rmp.resize();
            this.#rmp.resolvePromise('fullscreen');
          }, 1);
        }
      }
    }
  }

  destroy() {
    if (Env.hasScreenOrientationLockAPI) {
      window.screen.orientation.removeEventListener('change', this.#manageOrientationChangeFn);
    }
    document.removeEventListener('fullscreenchange', this.#fullscreenchangeFn);
  }

  init() {
    this.#rmp.dom.fullscreen = FW.createElement(
      'button',
      'rmp-fullscreen rmp-button rmp-i rmp-i-resize-full',
      null,
      null
    );
    this.#rmp.dom.fullscreenHint = FW.createElement(
      'div',
      'rmp-control-bar-hint rmp-color-bg',
      this.#rmp.labels.hint.enterFullscreen + ' (f)',
      this.#rmp.rtl
    );
    this.#rmp.dom.fullscreen.appendChild(this.#rmp.dom.fullscreenHint);
    if (this.#rmp.hideFullscreen) {
      this.#rmp.dom.fullscreen.classList.add('rmp-no-display');
    }
    this.#rmp.dom.controlBar.appendChild(this.#rmp.dom.fullscreen);
    this.#rmp.dom.container.appendChild(this.#rmp.dom.controlBar);
    // wire events for FS change
    const _fullscreenButtonInteractionFn = this.#fullscreenButtonInteraction.bind(this);
    if (this.#rmp.hasNativeFullscreenSupport) {
      if (Env.isIos[0]) {
        this.wireIosFullscreen();
      } else {
        this.#fullscreenchangeFn = this.#fullscreenchange.bind(this);
        document.addEventListener('fullscreenchange', this.#fullscreenchangeFn);
      }
    }
    FW.addEvents(['touchend', 'click'], this.#rmp.dom.fullscreen, _fullscreenButtonInteractionFn);

    if (this.#rmp.automaticFullscreenOnLandscape && Env.hasScreenOrientationLockAPI) {
      this.#manageOrientationChangeFn = this.#manageOrientationChange.bind(this);
      window.screen.orientation.addEventListener('change', this.#manageOrientationChangeFn);
    }
  }

}
